/**
 * CV Match Page V2
 * 
 * Find CVs matching specific requirements using AI-powered semantic search
 * Features per-item tier control (required/preferred/bonus)
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    AlertTitle,
    Grid,
    TextField,
    Chip,
    Stack,
    CircularProgress,
    MenuItem,
    IconButton,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import CVMatchingResults from '../components/CVMatchingResults';
import HelpIconButton from '../components/HelpIconButton';
import CVSourceToggle, { CVSource } from '../components/CVSourceToggle';
import { matchCVsManual, CVMatchingResult } from '../services/cvMatching';

// Types
type RequirementTier = 'required' | 'preferred' | 'bonus';

interface TieredItem {
    text: string;
    tier: RequirementTier;
}

// Tier colors
const TIER_COLORS = {
    required: { bg: '#b71c1c', text: 'white' },
    preferred: { bg: '#e65100', text: 'white' },
    bonus: { bg: '#2e7d32', text: 'white' }
};

const CVMatchPageV2: React.FC = () => {
    // CV Source selection
    const [cvSource, setCvSource] = useState<CVSource>('associate');
    
    // Search criteria state - now with per-item tiers
    const [geographic, setGeographic] = useState<TieredItem[]>([]);
    const [methods, setMethods] = useState<TieredItem[]>([]);
    const [languages, setLanguages] = useState<TieredItem[]>([]);
    const [sectors, setSectors] = useState<TieredItem[]>([]);
    const [educationFields, setEducationFields] = useState<TieredItem[]>([]);
    const [technicalTools, setTechnicalTools] = useState<TieredItem[]>([]);
    const [frameworks, setFrameworks] = useState<TieredItem[]>([]);
    const [clientTypes, setClientTypes] = useState<TieredItem[]>([]);
    
    // Single-value fields with their own tier
    const [yearsExperience, setYearsExperience] = useState('');
    const [yearsExperienceTier, setYearsExperienceTier] = useState<RequirementTier>('required');
    const [educationLevel, setEducationLevel] = useState('');
    const [educationLevelTier, setEducationLevelTier] = useState<RequirementTier>('required');
    
    // Free-text keywords (boost only, not a hard filter)
    const [freetextKeywords, setFreetextKeywords] = useState<string[]>([]);
    const [freetextLogic, setFreetextLogic] = useState<'AND' | 'OR'>('AND');
    
    // Input state for adding new items
    const [newInputs, setNewInputs] = useState<{[key: string]: string}>({});
    
    // Results state
    const [cvMatches, setCvMatches] = useState<CVMatchingResult | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    
    // Cycle tier: required -> preferred -> bonus -> required
    const cycleTier = useCallback((tier: RequirementTier): RequirementTier => {
        switch (tier) {
            case 'required': return 'preferred';
            case 'preferred': return 'bonus';
            case 'bonus': return 'required';
            default: return 'required';
        }
    }, []);
    
    // Handle chip click to cycle tier
    const handleChipClick = useCallback((
        items: TieredItem[], 
        setItems: (items: TieredItem[]) => void, 
        index: number
    ) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], tier: cycleTier(newItems[index].tier) };
        setItems(newItems);
    }, [cycleTier]);
    
    // Handle chip remove (right-click)
    const handleChipRemove = useCallback((
        items: TieredItem[], 
        setItems: (items: TieredItem[]) => void, 
        index: number
    ) => {
        setItems(items.filter((_, i) => i !== index));
    }, []);
    
    // Handle add new item
    const handleAddItem = useCallback((
        key: string,
        items: TieredItem[], 
        setItems: (items: TieredItem[]) => void
    ) => {
        const text = newInputs[key]?.trim();
        if (text && !items.some(item => item.text.toLowerCase() === text.toLowerCase())) {
            setItems([...items, { text, tier: 'required' }]);
            setNewInputs(prev => ({ ...prev, [key]: '' }));
        }
    }, [newInputs]);
    
    // Render tiered chip
    const renderTieredChip = (
        items: TieredItem[],
        setItems: (items: TieredItem[]) => void,
        item: TieredItem,
        index: number
    ) => {
        const colors = TIER_COLORS[item.tier];
        return (
            <Chip
                key={`${item.text}-${index}`}
                label={item.text}
                size="small"
                onClick={() => handleChipClick(items, setItems, index)}
                onContextMenu={(e) => {
                    e.preventDefault();
                    handleChipRemove(items, setItems, index);
                }}
                sx={{
                    cursor: 'pointer',
                    bgcolor: colors.bg,
                    color: colors.text,
                    '&:hover': { bgcolor: colors.bg, opacity: 0.85 }
                }}
            />
        );
    };
    
    // Render single-value tier chip (for years, education level, role)
    const renderSingleTierChip = (
        value: string,
        tier: RequirementTier,
        setTier: (tier: RequirementTier) => void,
        onRemove: () => void
    ) => {
        const colors = TIER_COLORS[tier];
        return (
            <Chip
                label={value}
                size="small"
                onClick={() => setTier(cycleTier(tier))}
                onDelete={onRemove}
                sx={{
                    cursor: 'pointer',
                    bgcolor: colors.bg,
                    color: colors.text,
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                    '&:hover': { bgcolor: colors.bg, opacity: 0.85 }
                }}
            />
        );
    };
    
    // Render category section with tiered chips
    const renderCategorySection = (
        key: string,
        label: string,
        items: TieredItem[],
        setItems: (items: TieredItem[]) => void,
        placeholder: string,
        helpKey?: string
    ) => (
        <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Typography>
                {helpKey && <HelpIconButton helpKey={helpKey} title={label} />}
            </Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1, minHeight: 32 }}>
                {items.map((item, i) => renderTieredChip(items, setItems, item, i))}
                {items.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No items. Add below.
                    </Typography>
                )}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={placeholder}
                    value={newInputs[key] || ''}
                    onChange={(e) => setNewInputs(prev => ({ ...prev, [key]: e.target.value }))}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') handleAddItem(key, items, setItems);
                    }}
                />
                <IconButton 
                    color="primary"
                    onClick={() => handleAddItem(key, items, setItems)}
                    disabled={!newInputs[key]?.trim()}
                >
                    <AddIcon />
                </IconButton>
            </Box>
        </Grid>
    );
    
    const handleSearch = async () => {
        // Build V2 requirements structure with per-item tiers
        const torRequirements = {
            requirements: {
                project_title: 'CV Search',
                geographic_experience: {
                    countries: geographic.map(g => ({ text: g.text, tier: g.tier })),
                    regions: []
                },
                methods_methodologies: { items: methods },
                language_requirements: { items: languages },
                sector_experience: { items: sectors },
                education_requirements: {
                    minimum_level: educationLevel || null,
                    minimum_level_tier: educationLevelTier,
                    fields: educationFields
                },
                experience_requirements: {
                    minimum_years: yearsExperience ? parseInt(yearsExperience.replace('+', '')) : null,
                    minimum_years_tier: yearsExperienceTier,
                    experience_types: []
                },
                technical_tools_software: { items: technicalTools },
                frameworks_standards: { items: frameworks },
                communication_stakeholder: { items: [] },
                professional_ethical: { items: [] },
                donor_experience: { items: clientTypes },
                team_requirements: {
                    role: null,
                    level: null,
                    team_size: null,
                    team_composition: null
                },
                freetext_keywords: freetextKeywords.length > 0 ? freetextKeywords : undefined,
                freetext_logic: freetextKeywords.length > 0 ? freetextLogic : undefined
            }
        };
        
        setIsSearching(true);
        setError(null);
        
        try {
            const result = await matchCVsManual(torRequirements, 12, cvSource, undefined, freetextKeywords, freetextLogic);
            setCvMatches(result);
        } catch (err) {
            console.error('CV search failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to search CVs');
        } finally {
            setIsSearching(false);
        }
    };
    
    const handleReset = () => {
        setGeographic([]);
        setMethods([]);
        setLanguages([]);
        setSectors([]);
        setEducationFields([]);
        setTechnicalTools([]);
        setFrameworks([]);
        setClientTypes([]);
        setYearsExperience('');
        setEducationLevel('');
        setFreetextKeywords([]);
        setFreetextLogic('AND');
        setNewInputs({});
        setCvMatches(null);
        setError(null);
    };
    
    // Parse boolean search string into individual terms
    // Supports: OR/AND-separated terms, quoted phrases, commas
    // e.g. 'ICT OR IT OR "Information Technology"' or 'ICT, IT, Information Technology'
    const parseBooleanSearch = useCallback((input: string): string[] => {
        const terms: string[] = [];
        let remaining = input.trim();
        
        // Extract quoted phrases first
        const quotedRegex = /"([^"]+)"/g;
        let match;
        while ((match = quotedRegex.exec(remaining)) !== null) {
            const phrase = match[1].trim();
            if (phrase) terms.push(phrase);
        }
        // Remove quoted phrases from remaining
        remaining = remaining.replace(quotedRegex, ' ');
        
        // Split by OR or AND (case-insensitive, with word boundaries)
        const parts = remaining.split(/\s+(?:OR|AND)\s+/i);
        for (const part of parts) {
            // Also split by commas for convenience
            const subParts = part.split(/,/).map(p => p.trim()).filter(p => p.length > 0);
            terms.push(...subParts);
        }
        
        // Clean up: remove empty strings, duplicates (case-insensitive)
        const seen = new Set<string>();
        return terms.filter(t => {
            const lower = t.toLowerCase();
            if (!t || seen.has(lower)) return false;
            seen.add(lower);
            return true;
        });
    }, []);
    
    // Handle adding a freetext keyword (with boolean parsing)
    const handleAddFreetextKeyword = useCallback(() => {
        const text = newInputs['freetext']?.trim();
        if (!text) return;
        
        // Parse for boolean operators and quoted phrases
        const parsedTerms = parseBooleanSearch(text);
        
        // Always clear input after processing
        setNewInputs(prev => ({ ...prev, freetext: '' }));
        
        // Add all parsed terms that don't already exist
        const newTerms = parsedTerms.filter(
            term => !freetextKeywords.some(k => k.toLowerCase() === term.toLowerCase())
        );
        
        if (newTerms.length > 0) {
            setFreetextKeywords(prev => [...prev, ...newTerms]);
            
            // Auto-set logic based on the LAST operator used in input
            // This handles mixed cases like "ICT OR IT AND Info" - last operator (AND) wins
            if (parsedTerms.length > 1) {
                const textLower = text.toLowerCase();
                const lastOrIndex = textLower.lastIndexOf(' or ');
                const lastAndIndex = textLower.lastIndexOf(' and ');
                
                if (lastOrIndex > lastAndIndex) {
                    setFreetextLogic('OR');
                } else if (lastAndIndex > lastOrIndex) {
                    setFreetextLogic('AND');
                }
                // If neither found (e.g., comma-separated), keep current logic
            }
        }
    }, [newInputs, freetextKeywords, parseBooleanSearch]);
    
    // Handle removing a freetext keyword
    const handleRemoveFreetextKeyword = useCallback((index: number) => {
        setFreetextKeywords(prev => prev.filter((_, i) => i !== index));
    }, []);
    
    const hasSearchCriteria = geographic.length > 0 || methods.length > 0 || languages.length > 0 || 
        sectors.length > 0 || educationFields.length > 0 || technicalTools.length > 0 || 
        frameworks.length > 0 || clientTypes.length > 0 ||
        yearsExperience || educationLevel || freetextKeywords.length > 0;
    
    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="large" />
                            CV Match
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Find CVs matching your specific requirements using AI-powered semantic search
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* CVSourceToggle hidden - IOD Staff is work in progress, defaulting to Associate */}
                        {/* <CVSourceToggle 
                            value={cvSource} 
                            onChange={setCvSource}
                            disabled={isSearching}
                        /> */}
                        <HelpIconButton 
                            helpKey="main" 
                            title="How CV Matching Works"
                            size="large"
                        />
                    </Box>
                </Box>
            </Box>
            
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {/* Search Form */}
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h6" gutterBottom>Search Criteria</Typography>
                
                {/* Legend */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">Click chip to cycle tier:</Typography>
                    <Chip label="Required" size="small" sx={{ bgcolor: TIER_COLORS.required.bg, color: 'white' }} />
                    <Typography variant="body2" color="text.secondary">→</Typography>
                    <Chip label="Preferred" size="small" sx={{ bgcolor: TIER_COLORS.preferred.bg, color: 'white' }} />
                    <Typography variant="body2" color="text.secondary">→</Typography>
                    <Chip label="Bonus" size="small" sx={{ bgcolor: TIER_COLORS.bonus.bg, color: 'white' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>| Right-click to remove</Typography>
                </Box>
                
                <Grid container spacing={3}>
                    {/* Row 1: Geographic Countries & Regions */}
                    {renderCategorySection('geographic', 'Geographic Countries & Regions', geographic, setGeographic, 
                        'Add geographic countries...', 'geographic')}
                    
                    {/* Methods & Methodologies */}
                    {renderCategorySection('methods', 'Methods & Methodologies', methods, setMethods,
                        'Add methods & methodologies...', 'methods')}
                    
                    {/* Row 2: Technical Tools & Frameworks */}
                    {renderCategorySection('technicalTools', 'Technical Tools & Software', technicalTools, setTechnicalTools,
                        'Add technical tools & software...', 'technicalTools')}
                    
                    {renderCategorySection('frameworks', 'Frameworks & Standards', frameworks, setFrameworks,
                        'Add frameworks & standards...', 'frameworks')}
                    
                    {/* Row 3: Languages & Sector Experience */}
                    {renderCategorySection('languages', 'Languages', languages, setLanguages,
                        'Add languages...', 'languages')}
                    
                    {renderCategorySection('sectors', 'Sector Experience', sectors, setSectors,
                        'Add sector experience...', 'sectors')}
                    
                    {/* Row 4: Donor Experience & Education Fields */}
                    {renderCategorySection('clientTypes', 'Donor Experience', clientTypes, setClientTypes,
                        'Add donor experience...', 'clientTypes')}
                    
                    {renderCategorySection('educationFields', 'Education Fields', educationFields, setEducationFields,
                        'Add education fields...', 'educationFields')}
                </Grid>
                
                {/* Single-value fields section */}
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {/* Years of Experience */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Minimum Years of Experience</Typography>
                            <HelpIconButton helpKey="yearsExperience" title="Years of Experience" />
                        </Box>
                        {yearsExperience ? (
                            <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                                {renderSingleTierChip(
                                    `${yearsExperience} years`,
                                    yearsExperienceTier,
                                    setYearsExperienceTier,
                                    () => setYearsExperience('')
                                )}
                            </Stack>
                        ) : (
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={yearsExperience}
                                onChange={(e) => setYearsExperience(e.target.value)}
                            >
                                <MenuItem value="">Select experience level</MenuItem>
                                <MenuItem value="5+">5+ years</MenuItem>
                                <MenuItem value="10+">10+ years</MenuItem>
                                <MenuItem value="15+">15+ years</MenuItem>
                                <MenuItem value="20+">20+ years</MenuItem>
                            </TextField>
                        )}
                    </Grid>
                    
                    {/* Education Level */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Minimum Education Level</Typography>
                            <HelpIconButton helpKey="educationLevel" title="Education Level" />
                        </Box>
                        {educationLevel ? (
                            <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                                {renderSingleTierChip(
                                    educationLevel,
                                    educationLevelTier,
                                    setEducationLevelTier,
                                    () => setEducationLevel('')
                                )}
                            </Stack>
                        ) : (
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={educationLevel}
                                onChange={(e) => setEducationLevel(e.target.value)}
                            >
                                <MenuItem value="">Select education level</MenuItem>
                                <MenuItem value="Bachelor's">Bachelor's Degree</MenuItem>
                                <MenuItem value="Master's">Master's Degree</MenuItem>
                                <MenuItem value="PhD">PhD/Doctorate</MenuItem>
                            </TextField>
                        )}
                    </Grid>
                    
                </Grid>
                
                {/* Free-Text Keywords Section */}
                <Grid container spacing={3} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Free-Text Keywords
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                (boost ranking — not a filter)
                            </Typography>
                            <HelpIconButton helpKey="freetextKeywords" title="Free-Text Keywords" />
                        </Box>
                        
                        {/* Show logic state when there are multiple keywords */}
                        {freetextKeywords.length > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                {freetextLogic === 'OR' 
                                    ? 'Boosting CVs matching any of these:' 
                                    : 'Boosting CVs matching all of these:'}
                            </Typography>
                        )}
                        
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1, minHeight: 32 }}>
                            {freetextKeywords.map((keyword, i) => (
                                <React.Fragment key={`freetext-${i}`}>
                                    <Chip
                                        label={keyword}
                                        size="small"
                                        onDelete={() => handleRemoveFreetextKeyword(i)}
                                        sx={{ bgcolor: '#1976d2', color: 'white', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                                    />
                                    {i < freetextKeywords.length - 1 && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                px: 0.5
                                            }}
                                        >
                                            {freetextLogic}
                                        </Typography>
                                    )}
                                </React.Fragment>
                            ))}
                        </Stack>
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="e.g., ICT OR IT OR &quot;Information Technology&quot;"
                                value={newInputs['freetext'] || ''}
                                onChange={(e) => setNewInputs(prev => ({ ...prev, freetext: e.target.value }))}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAddFreetextKeyword();
                                }}
                            />
                            <IconButton 
                                color="primary"
                                onClick={handleAddFreetextKeyword}
                                disabled={!newInputs['freetext']?.trim()}
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        startIcon={<RefreshIcon />}
                        disabled={!hasSearchCriteria}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                        disabled={isSearching || !hasSearchCriteria}
                        startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {isSearching ? 'Searching...' : 'Find Matching CVs'}
                    </Button>
                </Box>
            </Paper>
            
            {/* Results */}
            {cvMatches && cvMatches.matches.length > 0 && (
                <CVMatchingResults 
                    matches={cvMatches.matches}
                    additionalMatches={cvMatches.additional_matches}
                    searchCriteria={cvMatches.search_criteria}
                    selectedCategories={cvMatches.selected_categories}
                />
            )}
            
            {cvMatches && cvMatches.matches.length === 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>No CVs Found Matching All Required Criteria</AlertTitle>
                    
                    {cvMatches.failure_analysis ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Checked {cvMatches.failure_analysis.total_cvs_checked.toLocaleString()} CVs — none have all required criteria.
                            </Typography>
                            
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                                Consider changing these to Preferred (in order of impact):
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {/* Sort by DESCENDING failure count - highest blockers first */}
                                {[...cvMatches.failure_analysis.criteria_failure_counts]
                                    .sort((a, b) => b.failed_count - a.failed_count)
                                    .slice(0, 5)
                                    .map((item, idx) => (
                                        <Chip 
                                            key={idx}
                                            label={`${idx + 1}. ${item.criterion}`} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: TIER_COLORS.required.bg, 
                                                color: 'white'
                                            }}
                                        />
                                    ))}
                            </Box>
                            
                            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                💡 Click a red chip in your search criteria to change it to Preferred.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2">
                            Try broadening your search by changing some Required criteria to Preferred.
                        </Typography>
                    )}
                </Alert>
            )}
        </Box>
    );
};

export default CVMatchPageV2;
